import { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  Typography,
} from '@mui/material';
import { CLOUDER_TIERS, ROUTE_TYPES } from '@wareclouds/frontend-common/b2c';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RouteIcon from '@mui/icons-material/Route';

import getCitiesNames from '../../../../utils/getCitiesName';

import NumberFormatUnit from '../../../../utils/numberFormatUnit';
import { METER_TO_KILOMETER } from '../../../../config/consts/commons';
import useMobile from '../../../../hooks/useMobile';

import EnabledRouteMap from './EnabledRouteMap';
import EnabledRouteOrders from './EnabledRouteOrders';
import PriceBreakdownPopover from './PriceBreakdownPopover';

import { API_KEY } from '../../../../config/consts/commons';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../../utils';
import { CustomTooltip } from '../../../common';
import PickupPointsTable from './PickupPointsTable';

import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';

import env from '@/config/env';

function EnabledRouteCard({
  route,
  canTakeRoute,
  handleTakeRoute,
  isNewClouder = false,
}) {
  const CLOUDER_TIERS_KEYS = useMemo(
    () =>
      Object.fromEntries(Object.entries(CLOUDER_TIERS).map(([k, v]) => [v, k])),
    []
  );

  const {
    id_route,
    route_distance,
    route_distance_real,
    route_type,
    n_orders,
    n_packages,
    cities,
    pickup_points,
    price,
    orders,
    clouder,
    map_route_link,
    has_return_service,
    route_price: {
      price_cold_deliveries,
      price_pickups,
      price_base,
      price_oreti,
    } = {},
  } = route;

  const displayedRouteDistance =
    route_type === ROUTE_TYPES.CROSSDOCKING && route_distance_real != null
      ? route_distance_real
      : route_distance;

  const [isExpand, setIsExpand] = useState(false);
  const [mapLoadStatus, setMapLoadStatus] = useState(
    map_route_link ? 'idle' : 'unavailable'
  );
  const isMobile = useMobile();
  const { t } = useTranslation(['glossary', 'description', 'button']);

  const cityNames = useMemo(() => getCitiesNames(cities), [cities]);

  const isRouteOverMaximumForNewClouder = n_orders > env.MAX_ORDERS_NEW_CLOUDER;

  console.log({
    isRouteOverMaximumForNewClouder,
    env: env.MAX_ORDERS_NEW_CLOUDER,
  });

  useEffect(() => {
    setMapLoadStatus(map_route_link ? 'idle' : 'unavailable');
  }, [map_route_link]);

  const handleToggleExpand = () => {
    const nextIsExpand = !isExpand;

    if (nextIsExpand && map_route_link && mapLoadStatus === 'idle') {
      setMapLoadStatus('loading');
    }

    setIsExpand(nextIsExpand);
  };

  const shouldRenderMap =
    isExpand && map_route_link && ['loading', 'loaded'].includes(mapLoadStatus);

  const shouldRenderFallback =
    isExpand && (!map_route_link || mapLoadStatus === 'error');

  return (
    <Card sx={{ mb: 2, opacity: clouder ? '0.8' : '1', borderRadius: 2 }}>
      <CardContent>
        <Grid container>
          <Grid
            item
            container
            alignItems="flex-start"
            wrap="nowrap"
            spacing={{ xs: 1, sm: 2 }}
            sx={{ minWidth: 0 }}
          >
            <Grid xs={8} item sx={{ minWidth: 0 }}>
              <Grid
                item
                container
                spacing={{ xs: 0.5, sm: 1 }}
                alignItems="center"
              >
                <Grid xs item container alignItems="center">
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RouteIcon
                        sx={{
                          fontSize: isMobile ? 20 : 24,
                          color: 'primary.main',
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          fontWeight: 'bold',
                          color: 'primary.main',
                        }}
                      >
                        {id_route}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        columnGap: 0.5,
                      }}
                    >
                      <Chip
                        label={`${NumberFormatUnit({
                          value:
                            displayedRouteDistance / METER_TO_KILOMETER,
                        })} aprox`}
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{
                          fontSize: 12,
                          fontWeight: 'semibold',
                          color: 'text.secondary',
                        }}
                      />
                      <CustomTooltip
                        title={t('routes.distance_helper', {
                          ns: 'description',
                        })}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid
              xs
              item
              container
              justifyContent="flex-end"
              sx={{ flexShrink: 0 }}
            >
              <Grid item>
                {price_cold_deliveries || price_pickups || price_oreti ? (
                  <PriceBreakdownPopover
                    total={price}
                    basePrice={price_base}
                    pickups={price_pickups}
                    coldDeliveries={price_cold_deliveries}
                    oreti={price_oreti}
                    isMobile={isMobile}
                  />
                ) : (
                  <Chip
                    label={formatCurrency({ amount: price })}
                    size={isMobile ? 'small' : 'medium'}
                    color="success"
                    variant="outlined"
                    sx={{
                      borderRadius: 1,
                      fontWeight: 'bold',
                      fontSize: isMobile ? 13 : 14,
                      px: 1,
                      height: 'auto',
                      '& .MuiChip-label': {
                        fontSize: isMobile ? 13 : 14,
                        fontWeight: 'bold',
                        px: 1,
                        py: 0.5,
                      },
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>

          <Grid item container mt={3} flexDirection="column">
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant={isMobile ? 'body2' : 'body1'}>
                  <Box color="gray" component="span" sx={{ fontWeight: 600 }}>
                    {t('orders', { ns: 'glossary' })}:
                  </Box>{' '}
                  {n_orders}
                </Typography>
                {Boolean(has_return_service) && (
                  <Chip
                    size="small"
                    label={t('routes.enabled.includes_pickups', {
                      ns: 'description',
                    })}
                    sx={{
                      bgcolor: 'grey.200',
                      color: 'text.secondary',
                      fontWeight: 500,
                      height: 24,
                    }}
                  />
                )}
              </Box>
            </Grid>

            <Grid item>
              <Typography variant={isMobile ? 'body2' : 'body1'}>
                <Box color="gray" component="span" sx={{ fontWeight: 600 }}>
                  {t('packages', { ns: 'glossary' })}:
                </Box>{' '}
                {n_packages}
              </Typography>
            </Grid>

            <Grid item>
              <Typography
                data-testid="cities"
                variant={isMobile ? 'body2' : 'body1'}
              >
                <Box color="gray" component="span" sx={{ fontWeight: 600 }}>
                  {t('destinations', { ns: 'glossary' })}:
                </Box>{' '}
                {cityNames}
              </Typography>
            </Grid>

            <Grid item>
              <Typography variant={isMobile ? 'body2' : 'body1'}>
                <Box color="gray" component="span" sx={{ fontWeight: 600 }}>
                  {t('pickup_points', { ns: 'glossary' })}:
                </Box>{' '}
                {pickup_points.length}
              </Typography>
            </Grid>
          </Grid>

          {clouder && (
            <Grid xs={12} item mt={3}>
              <Alert variant="outlined" severity="info">
                <strong>
                  {t(`routes.clouders.${CLOUDER_TIERS_KEYS[clouder.id_tier]}`, {
                    ns: 'description',
                  })}
                </strong>
              </Alert>
            </Grid>
          )}

          <Grid item container mt={3} flexDirection="column">
            <Collapse in={isExpand} timeout={300} sx={{ width: '100%' }}>
              {shouldRenderMap && (
                <Grid item xs={12} my={2}>
                  <EnabledRouteMap
                    urlMap={`${map_route_link}&key=${API_KEY}`}
                    altImg={t('routes.info.map_alt_text', {
                      ns: 'description',
                      cities: cityNames,
                    })}
                    handleLoad={() => setMapLoadStatus('loaded')}
                    handleError={() => setMapLoadStatus('error')}
                    routeType={route.route_type}
                  />
                  <PickupPointsTable pickupPoints={pickup_points} />
                </Grid>
              )}

              {shouldRenderFallback && (
                <Grid item xs={12} my={2}>
                  <EnabledRouteOrders
                    showMore={isExpand}
                    pickupPoints={pickup_points}
                    orders={orders}
                  />
                </Grid>
              )}
            </Collapse>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 1,
                width: '100%',
                justifyContent: { xs: 'stretch', sm: 'space-between' },
              }}
            >
              <Button
                variant="contained"
                endIcon={isExpand ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="medium"
                onClick={handleToggleExpand}
                sx={{
                  fontSize: { xs: 12, sm: 13 },
                  color: 'black',
                  bgcolor: 'grey.300',
                  flex: { xs: '1 1 0', sm: '0 0 auto' },
                  minWidth: { xs: 0, sm: 160 },
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  px: { xs: 1.25, sm: 1.75 },
                  '& .MuiButton-endIcon': { ml: 0.5 },
                  '&:hover': {
                    bgcolor: 'grey.400',
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                    maxWidth: '100%',
                  }}
                >
                  {t('view_details', { ns: 'button' })}
                </Box>
              </Button>
              {isNewClouder && isRouteOverMaximumForNewClouder ? (
                <Button
                  variant="contained"
                  size="medium"
                  onClick={() => window.Intercom('show')}
                  startIcon={<ContactSupportOutlinedIcon />}
                  sx={{
                    fontSize: { xs: 12, sm: 13 },
                    flex: { xs: '1.2 1 0', sm: '0 0 auto' },
                    minWidth: { xs: 0, sm: 150 },
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    px: { xs: 1.5, sm: 2.25 },
                    backgroundColor: 'warning.main',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                      maxWidth: '100%',
                    }}
                  >
                    {t('support', { ns: 'button' })}
                  </Box>
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="medium"
                  disabled={!canTakeRoute || !!clouder}
                  onClick={() => handleTakeRoute(id_route)}
                  sx={{
                    fontSize: { xs: 12, sm: 13 },
                    flex: { xs: '1.2 1 0', sm: '0 0 auto' },
                    minWidth: { xs: 0, sm: 150 },
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    px: { xs: 1.5, sm: 2.25 },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                      maxWidth: '100%',
                    }}
                  >
                    {t('take_route', { ns: 'button' })}
                  </Box>
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default EnabledRouteCard;
